/**
 * カレンダー表示用イベント生成
 *
 * 指定期間のアクティビティを時間軸に置けるイベントへ変換する。
 * 日別カレンダー（1日分）と連続スクロールカレンダー（複数日分）で共有する。
 */

import { parseISO } from 'date-fns'

import type { ReadHistory } from '@/api/type'
import type { CardTransaction } from '@/api/types/card-transaction'
import type { GarminActivitySummary, GarminSleepRaw } from '@/api/types/garmin'
import type { OverlandVisit } from '@/api/types/overland'
import type { ReceiptSummary } from '@/api/types/receipt'
import type { TodoResponse } from '@/api/types/todo'
import type { WorkoutSessionListItem } from '@/api/types/workout'
import { getMotionConfig } from '@/lib/activity-format-utils'
import { parseUtcDate } from '@/lib/date-utils'
import { listSleepEpisodes } from '@/lib/garmin-sleep'
import type { MovementSessionWithVisits } from '@/lib/movement-session'

import type { DayData } from '../day-data'

// レシートは点の出来事なので、時間軸で拾える高さになるよう幅を与える
const RECEIPT_EVENT_MINUTES = 15
/** 読書履歴は終了時刻を持たないので固定の長さで置く */
const READ_EVENT_MINUTES = 30
/** 完了TODOは点の出来事なので固定の長さで置く */
const TODO_EVENT_MINUTES = 15
/** 記録途中の筋トレセッションが時間軸を埋め尽くさないよう、終了時刻がなければこの長さにする */
const WORKOUT_FALLBACK_MINUTES = 60

export type CalendarEventType = 'visit' | 'movement' | 'sleep' | 'read' | 'receipt' | 'workout' | 'activity' | 'todo'

/** イベントのクリック時に必要な情報 */
export type CalendarEventProps = {
  type: CalendarEventType
  visit?: OverlandVisit
  session?: MovementSessionWithVisits
  receiptId?: number
  workoutSessionId?: number
}

/** 時間軸に配置するイベント */
export type ActivityCalendarEvent = {
  id: string
  title: string
  start: Date
  end: Date
  className: string
  props: CalendarEventProps
}

/** イベント生成に必要なデータ（期間での絞り込みはこのモジュール側で行う） */
export type CalendarEventSource = {
  rangeStart: Date
  rangeEnd: Date
  history: ReadHistory[]
  activities: GarminActivitySummary[]
  visits: OverlandVisit[]
  movementSessions: MovementSessionWithVisits[]
  sleeps: GarminSleepRaw[]
  receipts: ReceiptSummary[]
  /** レシートの無いカード利用 */
  cardTransactions: CardTransaction[]
  workouts: WorkoutSessionListItem[]
  todos: TodoResponse[]
}

/** その日の 00:00:00 と 23:59:59.999 */
export const getDayBounds = (date: Date): { dayStart: Date; dayEnd: Date } => {
  const dayStart = new Date(date)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(date)
  dayEnd.setHours(23, 59, 59, 999)
  return { dayStart, dayEnd }
}

export const buildCalendarEvents = ({
  rangeStart,
  rangeEnd,
  history,
  activities,
  visits,
  movementSessions,
  sleeps,
  receipts,
  cardTransactions,
  workouts,
  todos,
}: CalendarEventSource): ActivityCalendarEvent[] => {
  const result: ActivityCalendarEvent[] = []

  const isInRange = (date: Date): boolean => date >= rangeStart && date <= rangeEnd
  const overlapsRange = (start: Date, end: Date): boolean => start <= rangeEnd && end >= rangeStart
  const clamp = (date: Date): Date => {
    if (date < rangeStart) return rangeStart
    if (date > rangeEnd) return rangeEnd
    return date
  }

  const rangeHistory = history.filter((h) => h.started_at && isInRange(new Date(h.started_at)))
  const rangeActivities = activities.filter((a) => a.start_time && isInRange(new Date(a.start_time)))

  // 滞在中（departure_dateがない）Visitは現在時刻までの滞在として扱う
  const rangeVisits = visits
    .filter((v) => v.arrival_date)
    .map((v) => ({
      visit: v,
      arrival: parseISO(v.arrival_date!),
      departure: v.departure_date ? parseISO(v.departure_date) : new Date(),
    }))
    .filter(({ arrival, departure }) => overlapsRange(arrival, departure))

  // Visitイベント
  rangeVisits.forEach(({ visit, arrival, departure }) => {
    const isOngoing = !visit.departure_date
    const placeName = visit.place?.display_name ?? visit.place?.auto_name ?? '不明な場所'

    result.push({
      id: `visit-${visit.id}`,
      title: `📍 ${placeName}${isOngoing ? ' (滞在中)' : ''}`,
      start: clamp(arrival),
      end: clamp(departure),
      className: isOngoing ? 'calendar-event-visit-ongoing' : 'calendar-event-visit',
      props: { type: 'visit', visit },
    })
  })

  // 移動セッションイベント
  movementSessions.forEach((session) => {
    const startTime = parseISO(session.startTime)
    let endTime = parseISO(session.endTime)

    // 移動の終了時刻を隣接するVisitに合わせて調整
    for (const { arrival: visitStart } of rangeVisits) {
      // Visitの開始が移動期間内に重なっている場合、移動の終了をVisit開始に切り詰め
      if (visitStart > startTime && visitStart < endTime) {
        endTime = visitStart
      }
      // 移動終了後すぐにVisitが始まる場合（10分以内の隙間）、
      // 最小描画高さによる視覚的な重なりを防ぐため、移動終了をVisit開始にスナップ
      const gap = visitStart.getTime() - endTime.getTime()
      if (gap > 0 && gap < 10 * 60 * 1000) {
        endTime = visitStart
      }
    }

    if (!overlapsRange(startTime, endTime)) return

    const icon = getMotionConfig(session.motion).emoji

    result.push({
      id: session.id,
      title: `${icon} 移動 ${session.estimatedDistanceKm.toFixed(1)}km`,
      start: clamp(startTime),
      end: clamp(endTime),
      className: 'calendar-event-movement',
      props: { type: 'movement', session },
    })
  })

  // 睡眠イベント
  sleeps.forEach((sleep) => {
    const dto = sleep.dailySleepDTO
    if (!dto?.sleepStartTimestampGMT || !dto.sleepEndTimestampGMT) return

    const sleepStart = new Date(dto.sleepStartTimestampGMT)
    const sleepEnd = new Date(dto.sleepEndTimestampGMT)
    if (!overlapsRange(sleepStart, sleepEnd)) return

    const durationHours = dto.sleepTimeSeconds ? (dto.sleepTimeSeconds / 3600).toFixed(1) : '-'

    result.push({
      id: `sleep-${sleepStart.getTime()}`,
      title: `🌙 ${sleep.kind === 'nap' ? '昼寝' : '睡眠'} ${durationHours}時間`,
      start: clamp(sleepStart),
      end: clamp(sleepEnd),
      className: 'calendar-event-sleep',
      props: { type: 'sleep' },
    })
  })

  // 読書履歴（滞在中のものも滞在イベントとは別のブロックとして置く）
  rangeHistory.forEach((h) => {
    const readTime = new Date(h.started_at)

    result.push({
      id: `read-${h.id}`,
      title: `📖 ${h.product_title}${h.episode_title ? ` - ${h.episode_title}` : ''}`,
      start: readTime,
      end: clamp(new Date(readTime.getTime() + READ_EVENT_MINUTES * 60 * 1000)),
      className: 'calendar-event-read',
      props: { type: 'read' },
    })
  })

  // レシートイベント（購入時刻が印字されているものだけ時間軸に置ける）
  receipts.forEach((receipt) => {
    if (!receipt.purchased_at) return
    const purchasedAt = parseUtcDate(receipt.purchased_at)
    if (!isInRange(purchasedAt)) return
    const amount = receipt.total_amount === null ? '金額不明' : `¥${receipt.total_amount.toLocaleString()}`

    result.push({
      id: `receipt-${receipt.id}`,
      title: `🧾 ${amount} ${receipt.store_name || '店名なし'}`,
      start: purchasedAt,
      end: clamp(new Date(purchasedAt.getTime() + RECEIPT_EVENT_MINUTES * 60 * 1000)),
      className: 'calendar-event-receipt',
      props: { type: 'receipt', receiptId: receipt.id },
    })
  })

  // レシートの無いカード利用（時刻の無い売上到着分は置けない）。絞り込みはレシートと一緒にする
  cardTransactions.forEach((tx) => {
    if (!tx.used_at) return
    const usedAt = parseUtcDate(tx.used_at)
    if (!isInRange(usedAt)) return

    result.push({
      id: `card-${tx.id}`,
      title: `💳 ¥${tx.amount.toLocaleString()} ${tx.merchant}`,
      start: usedAt,
      end: clamp(new Date(usedAt.getTime() + RECEIPT_EVENT_MINUTES * 60 * 1000)),
      className: 'calendar-event-receipt',
      props: { type: 'receipt' },
    })
  })

  // Garmin アクティビティ（滞在の外で走った記録も拾えるよう単独のブロックで置く）
  rangeActivities.forEach((activity) => {
    const start = new Date(activity.start_time)
    const durationMin = activity.duration_seconds ? Math.round(activity.duration_seconds / 60) : 0
    const end = new Date(start.getTime() + Math.max(durationMin, 1) * 60 * 1000)

    result.push({
      id: `activity-${activity.activity_id}`,
      title: `💪 ${activity.activity_name}${durationMin > 0 ? ` ${durationMin}分` : ''}`,
      start,
      end: clamp(end),
      className: 'calendar-event-activity',
      props: { type: 'activity' },
    })
  })

  // 筋トレセッション
  workouts.forEach((workout) => {
    const start = parseUtcDate(workout.started_at)
    const end = workout.ended_at
      ? parseUtcDate(workout.ended_at)
      : new Date(start.getTime() + WORKOUT_FALLBACK_MINUTES * 60 * 1000)
    if (!overlapsRange(start, end)) return

    const name = workout.name || workout.template_name || '筋トレ'
    const volume = workout.total_volume_kg > 0 ? ` ${Math.round(workout.total_volume_kg).toLocaleString()}kg` : ''

    result.push({
      id: `workout-${workout.id}`,
      title: `🏋️ ${name}\n${workout.set_count}セット${volume}`,
      start: clamp(start),
      end: clamp(end),
      className: 'calendar-event-workout',
      props: { type: 'workout', workoutSessionId: workout.id },
    })
  })

  // 完了したTODO（未完了のものも一緒に返ってくるので完了時刻を持つものだけ拾う）
  todos.forEach((todo) => {
    if (!todo.completed_at) return
    const completedAt = parseUtcDate(todo.completed_at)
    if (!isInRange(completedAt)) return

    result.push({
      id: `todo-${todo.id}`,
      title: `✅ ${todo.title}`,
      start: completedAt,
      end: clamp(new Date(completedAt.getTime() + TODO_EVENT_MINUTES * 60 * 1000)),
      className: 'calendar-event-todo',
      props: { type: 'todo' },
    })
  })

  return result
}

// 日をまたぐデータは複数日の取得結果に重複して現れるため、idで一意にする
const uniqueBy = <T, K>(items: T[], getKey: (item: T) => K): T[] => {
  const map = new Map<K, T>()
  for (const item of items) {
    map.set(getKey(item), item)
  }
  return Array.from(map.values())
}

/** 日別データ（1日でも複数日でも）をまとめてイベントに変換する */
export const buildEventsFromDays = (
  days: DayData[],
  extras: {
    activities: GarminActivitySummary[]
    workouts: WorkoutSessionListItem[]
    todos: TodoResponse[]
  },
  rangeStart: Date,
  rangeEnd: Date,
): ActivityCalendarEvent[] =>
  buildCalendarEvents({
    rangeStart,
    rangeEnd,
    activities: extras.activities,
    workouts: extras.workouts,
    todos: extras.todos,
    history: uniqueBy(
      days.flatMap((day) => day.readHistory),
      (h) => h.id,
    ),
    visits: uniqueBy(
      days.flatMap((day) => day.visits),
      (v) => v.id,
    ),
    movementSessions: days.flatMap((day) => day.movementSessions),
    sleeps: uniqueBy(
      days.flatMap((day) => listSleepEpisodes(day.sleep)),
      (s) => s.dailySleepDTO?.sleepStartTimestampGMT ?? 0,
    ),
    receipts: uniqueBy(
      days.flatMap((day) => day.receipts),
      (r) => r.id,
    ),
    cardTransactions: uniqueBy(
      days.flatMap((day) => day.cardTransactions),
      (tx) => tx.id,
    ),
  })
